import { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "@emotion/styled";
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
} from "@mui/material";
import {
  CreateOrUpdateInvestmentParams,
  CreateOrUpdateInvestmentParamsTypeEnum,
  Investment,
  InvestmentTypeEnum,
  V1ListCurrenciesTypeEnum,
} from "@rentablo/api/apiv1";
import { LoadingButton } from "@rentablo/components";
import { CurrencySelect } from "@components";
import { isIsinError, useIsinValidation } from "@hooks";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: ${({ theme }) => theme.spacing(4)};
  gap: ${({ theme }) => theme.spacing(6)};

  ${({ theme }) => theme.breakpoints.up("sm")} {
    width: 50%;
  }
`;

interface Props {
  investment: Investment;
  isSaving: boolean;
  error: AxiosApiError | null;
  onSave: (data: CreateOrUpdateInvestmentParams) => void;
}

const investmentTypes: CreateOrUpdateInvestmentParamsTypeEnum[] = [
  "11_stock",
  "21_fund",
  "22_etf",
  "31_bond",
  "51_certos",
  "61_pmetals",
  "71_massets",
  "81_fcurr",
  "82_crypto",
  "91_managed",
];

export const InvestmentEditAdvancedTabContainer = ({
  investment,
  isSaving,
  error,
  onSave,
}: Props) => {
  const { t } = useTranslation();
  const { isIsinRequired, isIsinValid } = useIsinValidation(investment);
  const hasIsinError = isIsinError(error);
  const [type, setType] = useState<InvestmentTypeEnum>(investment.type);
  const [isin, setIsin] = useState(isIsinValid ? (investment.isin ?? "") : "");
  const [wkn, setWkn] = useState(investment.wkn);
  const [currency, setCurrency] = useState(investment.currency);

  const handleSave = () => {
    onSave({
      type: type as CreateOrUpdateInvestmentParamsTypeEnum,
      isin,
      wkn,
      currency,
    });
  };

  return (
    <Container>
      <FormControl>
        <InputLabel>
          {t("investmentEditPage.advancedTab.investmentType")}
        </InputLabel>
        <Select
          displayEmpty
          fullWidth
          value={type}
          onChange={(event) =>
            setType(event.target.value as InvestmentTypeEnum)
          }
        >
          {investmentTypes.map((item) => (
            <MenuItem key={item} value={item}>
              {t(`investmentTypes.${item}`)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        value={isin}
        onChange={(event) => setIsin(event.target.value)}
        label={t("investmentEditPage.advancedTab.isin")}
        required={isIsinRequired}
        error={hasIsinError}
        helperText={hasIsinError ? t("investmentsPage.isinInvalid") : undefined}
      />

      <TextField
        value={wkn}
        onChange={(event) => setWkn(event.target.value)}
        label={t("investmentEditPage.advancedTab.wkn")}
      />

      <CurrencySelect
        label={t("investmentEditPage.advancedTab.investmentCurrency")}
        value={currency}
        onChange={setCurrency}
        type={[V1ListCurrenciesTypeEnum.STANDARD]}
      />
      <div>
        <LoadingButton
          isFetching={isSaving}
          variant="primary"
          disabled={isSaving || (isIsinRequired && !isin.trim())}
          onClick={handleSave}
        >
          {t("investmentEditPage.advancedTab.saveButton")}
        </LoadingButton>
      </div>
    </Container>
  );
};
